import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import * as invoicesApi from './invoices.api';

import type {
  CreateBankAccount,
  UpdateBankAccount,
  CreateCreditNoteRequest,
  MarkCreditNoteUsedRequest,
  CreateFullInvoiceRequest,
  CreateInvoiceRequest,
  CreditNoteDetail,
  CreditNoteListQuery,
  GstOutputRegisterQuery,
  TcsRegisterQuery,
  InvoiceGenerateRequest,
  InvoiceGenerateResponse,
  InvoiceListQuery,
  InvoicePreviewRequest,
  InvoicePreviewResponse,
  RecordReceiptRequest,
  RecordReceiptResponse,
  RecordSupplierPaymentRequest,
  RecordSupplierPaymentResponse,
  SaveSupplierPaymentPlanRequest,
  SupplierPaymentReportQuery,
  SupplierPaymentStatusQuery,
  SupplierPaymentScheduleQuery,
  AgentReportCardQuery,
  SalesRegisterQuery,
  ClientOutstandingQuery,
  SupplierPaidOutstandingQuery,
  BalanceDueReportQuery,
  BankStatementQuery,
  OperationPendingQuery,
  DepositBookingQuery,
} from '@utazzo/shared';

/** Admin/finance invoice list. Query key carries the full filter set. */
export const useInvoicesList = (query: InvoiceListQuery) =>
  useQuery({
    queryKey: ['invoices', 'list', query],
    queryFn: () => invoicesApi.listInvoices(query),
    staleTime: 15_000,
  });

/** Single invoice detail (lines, tax lines, billing entity, buyer, bank). */
export const useInvoice = (id: string | undefined) =>
  useQuery({
    queryKey: ['invoices', 'detail', id],
    queryFn: () => invoicesApi.getInvoice(id as string),
    enabled: Boolean(id),
  });

/**
 * Editable state of an invoice, shaped to hydrate the full create-invoice form
 * in edit mode. Own query key so it stays independent of the read-side detail.
 * Never re-fetched while editing (the form owns the draft after hydration).
 */
export const useInvoiceEditData = (id: string | undefined) =>
  useQuery({
    queryKey: ['invoices', 'edit-data', id],
    queryFn: () => invoicesApi.getInvoiceEditData(id as string),
    enabled: Boolean(id),
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });

/**
 * Generate a proforma invoice from a booking (by number or uuid). Invalidates
 * the list on success and toasts either outcome; the caller navigates to the
 * new invoice using the returned id.
 */
export const useGenerateInvoice = () => {
  const qc = useQueryClient();
  return useMutation<InvoiceGenerateResponse, unknown, InvoiceGenerateRequest>({
    mutationFn: (body: InvoiceGenerateRequest) => invoicesApi.generateInvoice(body),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['invoices', 'list'] });
      toast.success(`Invoice ${res.invoiceNumber} generated`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to generate invoice'),
  });
};

/**
 * Create an invoice directly from the form (no booking). Invalidates the list
 * on success and toasts either outcome; the caller navigates to the new
 * invoice using the returned id.
 */
export const useCreateInvoice = () => {
  const qc = useQueryClient();
  return useMutation<InvoiceGenerateResponse, unknown, CreateInvoiceRequest>({
    mutationFn: (body: CreateInvoiceRequest) => invoicesApi.createInvoice(body),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['invoices', 'list'] });
      toast.success(`Invoice ${res.invoiceNumber} created`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to create invoice'),
  });
};

/** Static options for the full create-invoice form (products/slabs/modes). */
export const useInvoiceFormOptions = () =>
  useQuery({
    queryKey: ['invoices', 'form-options'],
    queryFn: () => invoicesApi.getInvoiceFormOptions(),
    staleTime: 5 * 60_000,
  });

/** Distinct travel-agent names for the invoice-list agent filter. */
export const useInvoiceAgents = () =>
  useQuery({
    queryKey: ['invoices', 'agents'],
    queryFn: () => invoicesApi.listInvoiceAgents(),
    staleTime: 5 * 60_000,
  });

/**
 * Live GST preview mutation - the create-invoice form debounces amount/slab
 * changes and calls `.mutate` to refresh the CGST/SGST/IGST + gross display.
 * Kept as a mutation (not a query) so the caller controls exactly when it
 * fires and can read `isPending` for a "computing..." state.
 */
export const useInvoicePreview = () =>
  useMutation<InvoicePreviewResponse, unknown, InvoicePreviewRequest>({
    mutationFn: (body: InvoicePreviewRequest) => invoicesApi.previewInvoice(body),
  });

/**
 * Create an invoice from the full legacy form (11 sections). Invalidates the
 * list and toasts either outcome; the caller navigates to the new invoice
 * using the returned id.
 */
export const useCreateFullInvoice = () => {
  const qc = useQueryClient();
  return useMutation<InvoiceGenerateResponse, unknown, CreateFullInvoiceRequest>({
    mutationFn: (body: CreateFullInvoiceRequest) => invoicesApi.createFullInvoice(body),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['invoices', 'list'] });
      toast.success(`Invoice ${res.invoiceNumber} created`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to create invoice'),
  });
};

/**
 * Edit an existing PROFORMA invoice from the full form (PATCH). Sends the SAME
 * `CreateFullInvoiceRequest` payload as create; on success invalidates the
 * invoice detail, edit-data, ledger and the list (everything can change), then
 * toasts. The caller navigates to the invoice using the returned id.
 */
export const useUpdateInvoice = (id: string | undefined) => {
  const qc = useQueryClient();
  return useMutation<InvoiceGenerateResponse, unknown, CreateFullInvoiceRequest>({
    mutationFn: (body: CreateFullInvoiceRequest) => invoicesApi.updateInvoice(id as string, body),
    onSuccess: (res) => {
      // refetchType 'all' so the detail/edit-data/ledger refetch even while
      // inactive. The global staleTime is 30s, so a plain invalidate only marks
      // the (currently inactive) detail query stale; navigating straight to the
      // invoice could then show the pre-edit amount/supplier until a manual
      // refresh. Forcing the refetch makes the invoice reflect the save at once.
      qc.invalidateQueries({ queryKey: ['invoices', 'detail', id], refetchType: 'all' });
      qc.invalidateQueries({ queryKey: ['invoices', 'edit-data', id], refetchType: 'all' });
      qc.invalidateQueries({ queryKey: ['invoices', 'ledger', id], refetchType: 'all' });
      qc.invalidateQueries({ queryKey: ['invoices', 'list'] });
      toast.success(`Invoice ${res.invoiceNumber} saved`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to save invoice'),
  });
};

/**
 * Payments ledger + activity log for one invoice (receipts, events, running
 * paid/due summary). Kept as its own query key so recording a payment can
 * invalidate it independently of the invoice detail.
 */
export const useInvoiceLedger = (id: string | undefined) =>
  useQuery({
    queryKey: ['invoices', 'ledger', id],
    queryFn: () => invoicesApi.getInvoiceLedger(id as string),
    enabled: Boolean(id),
  });

/**
 * Record a payment receipt against an invoice. On success invalidates the
 * ledger, the invoice detail and the list (paid/due + status can all change),
 * then toasts - a plain "Payment recorded", or a tax-invoice notice when the
 * payment settled the invoice in full and the server auto-issued it.
 */
export const useRecordReceipt = (id: string | undefined) => {
  const qc = useQueryClient();
  return useMutation<RecordReceiptResponse, unknown, RecordReceiptRequest>({
    mutationFn: (body: RecordReceiptRequest) =>
      invoicesApi.recordInvoiceReceipt(id as string, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices', 'ledger', id] });
      qc.invalidateQueries({ queryKey: ['invoices', 'detail', id] });
      qc.invalidateQueries({ queryKey: ['invoices', 'list'] });
      toast.success('Payment recorded');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to record payment'),
  });
};

/** Manually convert a single proforma to a numbered tax invoice, dated issueDate. */
export const useConvertToTaxInvoice = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, issueDate }: { id: string; issueDate?: string }) =>
      invoicesApi.convertToTaxInvoice(id, issueDate),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['invoices', 'detail', res.id] });
      qc.invalidateQueries({ queryKey: ['invoices', 'list'] });
      toast.success(`Tax invoice ${res.taxInvoiceNumber} issued`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to convert invoice'),
  });
};

/** Fully cancel an invoice (legacy isCancel). */
export const useCancelInvoice = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      invoicesApi.cancelInvoice(id, reason),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['invoices', 'detail', res.id] });
      qc.invalidateQueries({ queryKey: ['invoices', 'ledger', res.id] });
      qc.invalidateQueries({ queryKey: ['invoices', 'list'] });
      toast.success('Invoice cancelled');
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to cancel invoice'),
  });
};

/** Bulk-convert selected proformas to tax invoices (sequential numbering). */
export const useConvertManyToTaxInvoice = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ invoiceIds, issueDate }: { invoiceIds: string[]; issueDate?: string }) =>
      invoicesApi.convertManyToTaxInvoice(invoiceIds, issueDate),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['invoices', 'list'] });
      const skipped = res.skippedCount > 0 ? `, ${res.skippedCount} skipped` : '';
      toast.success(`${res.convertedCount} converted to tax invoice${skipped}`);
    },
    onError: (err: unknown) => toast.error(extractMessage(err) ?? 'Failed to convert invoices'),
  });
};

// ----- v2: credit notes -----

/** Credit notes issued against an invoice. */
export const useCreditNotes = (invoiceId: string | undefined) =>
  useQuery({
    queryKey: ['invoices', 'credit-notes', invoiceId],
    queryFn: () => invoicesApi.listCreditNotes(invoiceId as string),
    enabled: Boolean(invoiceId),
  });

/** Issue a credit note; invalidates the invoice detail, ledger and CN list. */
export const useIssueCreditNote = (invoiceId: string | undefined) => {
  const qc = useQueryClient();
  return useMutation<CreditNoteDetail, unknown, CreateCreditNoteRequest>({
    mutationFn: (body) => invoicesApi.issueCreditNote(invoiceId as string, body),
    onSuccess: (cn) => {
      qc.invalidateQueries({ queryKey: ['invoices', 'credit-notes', invoiceId] });
      qc.invalidateQueries({ queryKey: ['invoices', 'detail', invoiceId] });
      qc.invalidateQueries({ queryKey: ['invoices', 'ledger', invoiceId] });
      qc.invalidateQueries({ queryKey: ['invoices', 'list'] });
      toast.success(`Credit note ${cn.creditNoteNumber} issued`);
    },
    onError: (err) => toast.error(extractMessage(err) ?? 'Failed to issue credit note'),
  });
};

/** Cancel a credit note; invalidates the same views. */
export const useCancelCreditNote = (invoiceId: string | undefined) => {
  const qc = useQueryClient();
  return useMutation<CreditNoteDetail, unknown, string>({
    mutationFn: (id) => invoicesApi.cancelCreditNote(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices', 'credit-notes', invoiceId] });
      qc.invalidateQueries({ queryKey: ['invoices', 'detail', invoiceId] });
      qc.invalidateQueries({ queryKey: ['invoices', 'ledger', invoiceId] });
      qc.invalidateQueries({ queryKey: ['invoices', 'list'] });
      toast.success('Credit note cancelled');
    },
    onError: (err) => toast.error(extractMessage(err) ?? 'Failed to cancel credit note'),
  });
};

/** Mark a credit note used (record-keeping); optional note + linked invoice. */
export const useMarkCreditNoteUsed = (invoiceId: string | undefined) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: MarkCreditNoteUsedRequest }) =>
      invoicesApi.markCreditNoteUsed(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices', 'credit-notes', invoiceId] });
      qc.invalidateQueries({ queryKey: ['credit-notes', 'list'] });
      toast.success('Credit note marked used');
    },
    onError: (err) => toast.error(extractMessage(err) ?? 'Failed to mark credit note used'),
  });
};

/** Revert a used credit note back to issued. */
export const useUnmarkCreditNoteUsed = (invoiceId: string | undefined) => {
  const qc = useQueryClient();
  return useMutation<CreditNoteDetail, unknown, string>({
    mutationFn: (id) => invoicesApi.unmarkCreditNoteUsed(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices', 'credit-notes', invoiceId] });
      qc.invalidateQueries({ queryKey: ['credit-notes', 'list'] });
      toast.success('Credit note reverted to issued');
    },
    onError: (err) => toast.error(extractMessage(err) ?? 'Failed to revert credit note'),
  });
};

/** Global credit-note listing for a period (finance report). */
export const useCreditNotesList = (query: CreditNoteListQuery) =>
  useQuery({
    queryKey: ['reports', 'credit-notes', query],
    queryFn: () => invoicesApi.getCreditNotesList(query),
  });

/** A single credit note (for the print sheet). */
export const useCreditNote = (id: string | undefined) =>
  useQuery({
    queryKey: ['credit-notes', 'detail', id],
    queryFn: () => invoicesApi.getCreditNote(id as string),
    enabled: Boolean(id),
  });

// ----- v2: supplier AP settlement -----

/** The supplier payable lines + settlement ledger for one invoice. */
export const useInvoiceSupplierLedger = (invoiceId: string | undefined) =>
  useQuery({
    queryKey: ['invoices', 'supplier-ledger', invoiceId],
    queryFn: () => invoicesApi.getInvoiceSupplierLedger(invoiceId as string),
    enabled: Boolean(invoiceId),
  });

/** Record a supplier payment; invalidates the invoice's supplier ledger. */
export const useRecordSupplierPayment = (invoiceId: string | undefined) => {
  const qc = useQueryClient();
  return useMutation<
    RecordSupplierPaymentResponse,
    unknown,
    { invoiceSupplierId: string; body: RecordSupplierPaymentRequest }
  >({
    mutationFn: ({ invoiceSupplierId, body }) =>
      invoicesApi.recordSupplierPayment(invoiceSupplierId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices', 'supplier-ledger', invoiceId] });
      qc.invalidateQueries({ queryKey: ['suppliers', 'ledger'] });
      qc.invalidateQueries({ queryKey: ['reports', 'supplier-outstanding'] });
      toast.success('Supplier payment recorded');
    },
    onError: (err) => toast.error(extractMessage(err) ?? 'Failed to record supplier payment'),
  });
};

/** Cross-invoice ledger for one supplier. */
export const useSupplierLedger = (
  supplierId: string | undefined,
  params: { from?: string; to?: string } = {},
) =>
  useQuery({
    queryKey: ['suppliers', 'ledger', supplierId, params],
    queryFn: () => invoicesApi.getSupplierLedger(supplierId as string, params),
    enabled: Boolean(supplierId),
  });

/** Supplier outstanding report. */
export const useSupplierOutstanding = (params: {
  from?: string;
  to?: string;
  supplierId?: string;
}) =>
  useQuery({
    queryKey: ['reports', 'supplier-outstanding', params],
    queryFn: () => invoicesApi.getSupplierOutstanding(params),
  });

/** Supplier payments report (what we HAVE paid) for a period. */
export const useSupplierPayments = (query: SupplierPaymentReportQuery) =>
  useQuery({
    queryKey: ['reports', 'supplier-payments', query],
    queryFn: () => invoicesApi.getSupplierPayments(query),
  });

export const useSupplierPaymentStatus = (query: SupplierPaymentStatusQuery) =>
  useQuery({
    queryKey: ['reports', 'supplier-payment-status', query],
    queryFn: () => invoicesApi.getSupplierPaymentStatus(query),
  });

export const useAgentReportCard = (query: AgentReportCardQuery, enabled = true) =>
  useQuery({
    queryKey: ['reports', 'agent-report-card', query],
    queryFn: () => invoicesApi.getAgentReportCard(query),
    enabled,
  });

export const useSalesRegister = (query: SalesRegisterQuery) =>
  useQuery({
    queryKey: ['reports', 'sales-register', query],
    queryFn: () => invoicesApi.getSalesRegister(query),
  });

export const useClientOutstanding = (query: ClientOutstandingQuery) =>
  useQuery({
    queryKey: ['reports', 'client-outstanding', query],
    queryFn: () => invoicesApi.getClientOutstanding(query),
  });

export const useSupplierPaidOutstanding = (query: SupplierPaidOutstandingQuery) =>
  useQuery({
    queryKey: ['reports', 'supplier-paid-outstanding', query],
    queryFn: () => invoicesApi.getSupplierPaidOutstanding(query),
  });

export const useBalanceDueReport = (query: BalanceDueReportQuery) =>
  useQuery({
    queryKey: ['reports', 'balance-due', query],
    queryFn: () => invoicesApi.getBalanceDueReport(query),
  });

export const useBankStatement = (query: BankStatementQuery) =>
  useQuery({
    queryKey: ['reports', 'bank-statement', query],
    queryFn: () => invoicesApi.getBankStatement(query),
  });

export const useOperationPending = (query: OperationPendingQuery) =>
  useQuery({
    queryKey: ['reports', 'operation-pending', query],
    queryFn: () => invoicesApi.getOperationPending(query),
  });

export const useDepositBooking = (query: DepositBookingQuery) =>
  useQuery({
    queryKey: ['reports', 'deposit-booking', query],
    queryFn: () => invoicesApi.getDepositBooking(query),
  });

/** Save (replace) a supplier line's installment plan; refreshes its ledger. */
export const useSaveSupplierPaymentPlan = (invoiceId: string | undefined) => {
  const qc = useQueryClient();
  return useMutation<
    void,
    unknown,
    { invoiceSupplierId: string; body: SaveSupplierPaymentPlanRequest }
  >({
    mutationFn: ({ invoiceSupplierId, body }) =>
      invoicesApi.saveSupplierPaymentPlan(invoiceSupplierId, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices', 'supplier-ledger', invoiceId] });
      qc.invalidateQueries({ queryKey: ['suppliers', 'ledger'] });
      qc.invalidateQueries({ queryKey: ['reports', 'supplier-payment-schedule'] });
      toast.success('Payment plan saved');
    },
    onError: (err) => toast.error(extractMessage(err) ?? 'Failed to save payment plan'),
  });
};

/** Working-capital schedule: planned supplier installments aged into buckets. */
export const useSupplierPaymentSchedule = (query: SupplierPaymentScheduleQuery) =>
  useQuery({
    queryKey: ['reports', 'supplier-payment-schedule', query],
    queryFn: () => invoicesApi.getSupplierPaymentSchedule(query),
  });

// ----- v2: GST output register -----

/** GST output register for a period (enabled once from+to are set). */
export const useGstOutputRegister = (query: Partial<GstOutputRegisterQuery>) =>
  useQuery({
    queryKey: ['reports', 'gst-output-register', query],
    queryFn: () => invoicesApi.getGstOutputRegister(query as GstOutputRegisterQuery),
    enabled: Boolean(query.from && query.to),
  });

/** TCS register for a period (enabled once from+to are set). */
export const useTcsRegister = (query: Partial<TcsRegisterQuery>) =>
  useQuery({
    queryKey: ['reports', 'tcs-register', query],
    queryFn: () => invoicesApi.getTcsRegister(query as TcsRegisterQuery),
    enabled: Boolean(query.from && query.to),
  });

const extractMessage = (err: unknown): string | null => {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown; details?: { message?: unknown } };
    if (e.details && typeof e.details.message === 'string') return e.details.message;
    if (typeof e.message === 'string') return e.message;
  }
  if (err instanceof Error) return err.message;
  return null;
};

// ----- Multi-bank + void -----

/** Active company bank accounts for the invoice / payment dropdowns. */
export const useBankAccountOptions = () =>
  useQuery({
    queryKey: ['bank-accounts', 'options'],
    queryFn: () => invoicesApi.getBankAccountOptions(),
    staleTime: 60_000,
  });

/** All company bank accounts (settings manager). */
export const useBankAccounts = () =>
  useQuery({
    queryKey: ['bank-accounts', 'list'],
    queryFn: () => invoicesApi.listBankAccounts(),
  });

const invalidateBanks = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries({ queryKey: ['bank-accounts', 'list'] });
  qc.invalidateQueries({ queryKey: ['bank-accounts', 'options'] });
};

export const useCreateBankAccount = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateBankAccount) => invoicesApi.createBankAccount(body),
    onSuccess: () => {
      invalidateBanks(qc);
      toast.success('Bank account added');
    },
    onError: (err) => toast.error(extractMessage(err) ?? 'Failed to add bank account'),
  });
};

export const useUpdateBankAccount = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateBankAccount }) =>
      invoicesApi.updateBankAccount(id, body),
    onSuccess: () => {
      invalidateBanks(qc);
      toast.success('Bank account updated');
    },
    onError: (err) => toast.error(extractMessage(err) ?? 'Failed to update bank account'),
  });
};

export const useSetDefaultBankAccount = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => invoicesApi.setDefaultBankAccount(id),
    onSuccess: () => {
      invalidateBanks(qc);
      toast.success('Default bank account set');
    },
    onError: (err) => toast.error(extractMessage(err) ?? 'Failed to set default'),
  });
};

export const useRemoveBankAccount = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => invoicesApi.removeBankAccount(id),
    onSuccess: () => {
      invalidateBanks(qc);
      toast.success('Bank account removed');
    },
    onError: (err) => toast.error(extractMessage(err) ?? 'Failed to remove bank account'),
  });
};

/** Void a customer receipt; refreshes the invoice ledger + detail. */
export const useVoidReceipt = (invoiceId: string | undefined) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (receiptId: string) => invoicesApi.voidReceipt(invoiceId as string, receiptId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices', 'ledger', invoiceId] });
      qc.invalidateQueries({ queryKey: ['invoices', 'detail', invoiceId] });
      qc.invalidateQueries({ queryKey: ['invoices', 'list'] });
      toast.success('Payment voided');
    },
    onError: (err) => toast.error(extractMessage(err) ?? 'Failed to void payment'),
  });
};

/** Void a supplier payment; refreshes the invoice's supplier ledger. */
export const useVoidSupplierPayment = (invoiceId: string | undefined) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (paymentId: string) => invoicesApi.voidSupplierPayment(paymentId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices', 'supplier-ledger', invoiceId] });
      qc.invalidateQueries({ queryKey: ['suppliers', 'ledger'] });
      toast.success('Supplier payment voided');
    },
    onError: (err) => toast.error(extractMessage(err) ?? 'Failed to void supplier payment'),
  });
};
